import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Plus, Loader2, Send, Download,
  ChevronRight, Trash2, X, Clock, CheckCircle,
  Search, ArrowUpDown, SlidersHorizontal, Eye, Printer,
  Receipt, Building2, Calendar, Mail, DollarSign, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { useTranslation } from 'react-i18next';
import { invoicesApi } from '../../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Invoice {
  id: string;
  invoice_number: string;
  case_id?: string;
  case_ids?: string[];
  case_name?: string;
  client_name?: string;
  client_email?: string;
  total_amount?: number;
  total?: number;
  subtotal?: number;
  vat?: number;
  status: string;
  due_date: string;
  issue_date?: string;
  created_at: string;
  items?: any[];
  line_items?: any[];
  notes?: string;
  currency?: string;
}

interface Case {
  id: string;
  topic: string;
  client_name?: string;
}

interface InvoicingViewProps {
  cases: Case[];
  user: any;
  onRefresh: () => void;
}

const InvoicingViewMinimal: React.FC<InvoicingViewProps> = ({ cases, user, onRefresh }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'en';
  
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    case_id: '',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [{ description: '', hours: '', rate: user?.settings?.default_hourly_rate?.toString() || '400' }]
  });

  // Translations
  const tx = currentLanguage === 'no' ? {
    title: 'Fakturering',
    subtitle: 'Administrer og spor fakturaer',
    totalValue: 'Total verdi',
    collected: 'Innbetalt',
    outstanding: 'Utestående',
    drafts: 'Utkast',
    all: 'Alle',
    draft: 'Utkast',
    sent: 'Sendt',
    paid: 'Betalt',
    overdue: 'Forfalt',
    newInvoice: 'Ny faktura',
    noInvoices: 'Ingen fakturaer ennå',
    noInvoicesDesc: 'Opprett din første faktura for å komme i gang',
    dueDate: 'Forfall',
    invoiceDetails: 'Fakturadetaljer',
    client: 'Klient',
    case: 'Sak',
    issueDate: 'Utstedt',
    subtotal: 'Subtotal',
    vat: 'MVA (25%)',
    total: 'Totalt',
    lineItems: 'Linjer',
    description: 'Beskrivelse',
    qty: 'Antall',
    rate: 'Sats',
    amount: 'Beløp',
    send: 'Send faktura',
    markPaid: 'Merk som betalt',
    delete: 'Slett',
    invoiceSent: 'Faktura sendt',
    invoicePaid: 'Merket som betalt',
    invoiceDeleted: 'Faktura slettet',
    couldNotSend: 'Kunne ikke sende',
    couldNotMarkPaid: 'Kunne ikke merke',
    couldNotDelete: 'Kunne ikke slette',
    confirmDelete: 'Er du sikker på at du vil slette denne fakturaen?',
    selectCase: 'Velg sak',
    addItem: 'Legg til linje',
    hours: 'Timer',
    createInvoice: 'Opprett faktura',
    cancel: 'Avbryt',
    search: 'Søk etter klient eller fakturanummer...',
    preview: 'Forhåndsvis',
    print: 'Skriv ut',
    invoice: 'Faktura',
    to: 'Til',
    from: 'Fra',
    invoiceNo: 'Fakturanr',
    date: 'Dato',
    dueBy: 'Forfallsdato',
    thankYou: 'Takk for handelen!'
  } : {
    title: 'Invoicing',
    subtitle: 'Manage and track your invoices',
    totalValue: 'Total Value',
    collected: 'Collected',
    outstanding: 'Outstanding',
    drafts: 'Drafts',
    all: 'All',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    newInvoice: 'New Invoice',
    noInvoices: 'No invoices yet',
    noInvoicesDesc: 'Create your first invoice to get started',
    dueDate: 'Due',
    invoiceDetails: 'Invoice Details',
    client: 'Client',
    case: 'Case',
    issueDate: 'Issued',
    subtotal: 'Subtotal',
    vat: 'VAT (25%)',
    total: 'Total',
    lineItems: 'Items',
    description: 'Description',
    qty: 'Qty',
    rate: 'Rate',
    amount: 'Amount',
    send: 'Send Invoice',
    markPaid: 'Mark as Paid',
    delete: 'Delete',
    invoiceSent: 'Invoice sent',
    invoicePaid: 'Marked as paid',
    invoiceDeleted: 'Invoice deleted',
    couldNotSend: 'Could not send',
    couldNotMarkPaid: 'Could not mark',
    couldNotDelete: 'Could not delete',
    confirmDelete: 'Are you sure you want to delete this invoice?',
    selectCase: 'Select case',
    addItem: 'Add item',
    hours: 'Hours',
    createInvoice: 'Create Invoice',
    cancel: 'Cancel',
    search: 'Search by client or invoice number...',
    preview: 'Preview',
    print: 'Print',
    invoice: 'Invoice',
    to: 'Bill To',
    from: 'From',
    invoiceNo: 'Invoice #',
    date: 'Date',
    dueBy: 'Due Date',
    thankYou: 'Thank you for your business!'
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      setInvoices(response.data?.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceAmount = (inv: Invoice): number => {
    return inv.total_amount || (inv as any).total || 0;
  };

  const getSimpleInvoiceNumber = (fullNumber: string) => {
    if (!fullNumber) return '';
    const parts = fullNumber.split('-');
    return parts.length > 0 ? `#${parts[parts.length - 1]}` : fullNumber;
  };

  const handleSendInvoice = async (id: string) => {
    try {
      await axios.post(`${API}/invoices/${id}/send`, {}, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      toast.success(tx.invoiceSent);
      fetchInvoices();
      if (selectedInvoice?.id === id) {
        setSelectedInvoice(prev => prev ? { ...prev, status: 'SENT' } : null);
      }
    } catch (error) {
      toast.error(tx.couldNotSend);
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await axios.post(`${API}/invoices/${id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      toast.success(tx.invoicePaid);
      fetchInvoices();
      if (selectedInvoice?.id === id) {
        setSelectedInvoice(prev => prev ? { ...prev, status: 'PAID' } : null);
      }
    } catch (error) {
      toast.error(tx.couldNotMarkPaid);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(tx.confirmDelete)) return;
    try {
      await axios.delete(`${API}/invoices/${id}`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      toast.success(tx.invoiceDeleted);
      fetchInvoices();
      if (selectedInvoice?.id === id) {
        setSelectedInvoice(null);
        setShowPdfPreview(false);
      }
    } catch (error) {
      toast.error(tx.couldNotDelete);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const items = formData.items.map(item => ({
        description: item.description,
        quantity: parseFloat(item.hours) || 0,
        unit_price: parseFloat(item.rate) || 400,
        vat_rate: 0.25
      }));
      
      await axios.post(`${API}/invoices/`, {
        case_id: formData.case_id,
        client_id: formData.case_id,
        line_items: items,
        due_days: Math.ceil((new Date(formData.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      }, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      
      toast.success('Invoice created');
      setShowForm(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error('Could not create invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      case_id: '',
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      items: [{ description: '', hours: '', rate: user?.settings?.default_hourly_rate?.toString() || '400' }]
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', hours: '', rate: user?.settings?.default_hourly_rate?.toString() || '400' }]
    });
  };

  const updateItem = (index: number, field: string, value: string) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index: number) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  // Stats
  const stats = useMemo(() => {
    const total = invoices.reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);
    const paid = invoices.filter(inv => inv.status?.toUpperCase() === 'PAID').reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);
    const outstanding = invoices.filter(inv => inv.status?.toUpperCase() === 'SENT').reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);
    const drafts = invoices.filter(inv => inv.status?.toUpperCase() === 'DRAFT').length;
    return { total, paid, outstanding, drafts };
  }, [invoices]);

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    let result = [...invoices];
    
    if (statusFilter !== 'all') {
      result = result.filter(inv => inv.status?.toUpperCase() === statusFilter.toUpperCase());
    }
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(inv => 
        inv.invoice_number?.toLowerCase().includes(q) ||
        inv.client_name?.toLowerCase().includes(q) ||
        getCaseName(inv.case_id || inv.case_ids?.[0])?.toLowerCase().includes(q)
      );
    }
    
    return result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [invoices, statusFilter, searchQuery]);

  const getCaseName = (caseId?: string) => {
    if (!caseId) return '';
    return cases.find(c => c.id === caseId)?.topic || '';
  };

  const formatCurrency = (amount: number) => {
    return '$' + new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const formatFullDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusStyle = (status: string) => {
    const s = status?.toUpperCase();
    switch (s) {
      case 'PAID': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'SENT': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'OVERDUE': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const getStatusLabel = (status: string) => {
    const s = status?.toUpperCase();
    switch (s) {
      case 'PAID': return tx.paid;
      case 'SENT': return tx.sent;
      case 'OVERDUE': return tx.overdue;
      default: return tx.draft;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-8 py-12">
        
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{tx.title}</h1>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {tx.newInvoice}
            </button>
          </div>
          <p className="text-slate-500">{tx.subtitle}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-10">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats.total)}</div>
            <div className="text-sm text-slate-500 mt-1">{tx.totalValue}</div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats.paid)}</div>
            <div className="text-sm text-emerald-600 mt-1">{tx.collected}</div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats.outstanding)}</div>
            <div className="text-sm text-amber-600 mt-1">{tx.outstanding}</div>
          </div>
          
          <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <FileText className="w-5 h-5 text-slate-500" />
              </div>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{stats.drafts}</div>
            <div className="text-sm text-slate-500 mt-1">{tx.drafts}</div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={tx.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all"
            />
          </div>

          <div className="flex items-center bg-slate-100 rounded-xl p-1">
            {[
              { key: 'all', label: tx.all },
              { key: 'draft', label: tx.draft },
              { key: 'sent', label: tx.sent },
              { key: 'paid', label: tx.paid }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  statusFilter === tab.key 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice List */}
        <div className="space-y-2">
          {filteredInvoices.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-1">{tx.noInvoices}</h3>
              <p className="text-slate-500 text-sm mb-6">{tx.noInvoicesDesc}</p>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {tx.newInvoice}
              </button>
            </div>
          ) : (
            filteredInvoices.map((invoice) => (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => { setSelectedInvoice(invoice); setShowPdfPreview(false); }}
                className="group flex items-center gap-4 px-5 py-4 bg-slate-50/50 hover:bg-slate-50 rounded-xl cursor-pointer transition-all border border-transparent hover:border-slate-200"
              >
                {/* Invoice Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold text-slate-900">
                      {getSimpleInvoiceNumber(invoice.invoice_number)}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span className="font-medium text-slate-700 truncate">
                      {invoice.client_name || 'Unknown Client'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 truncate mt-0.5">
                    {getCaseName(invoice.case_id || invoice.case_ids?.[0]) || 'No case linked'}
                  </p>
                </div>
                
                {/* Status Badge */}
                <span className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${getStatusStyle(invoice.status)}`}>
                  {getStatusLabel(invoice.status)}
                </span>
                
                {/* Due Date */}
                <div className="w-20 text-right hidden sm:block">
                  <p className="text-sm text-slate-500">{formatDate(invoice.due_date)}</p>
                </div>
                
                {/* Amount */}
                <div className="w-28 text-right">
                  <p className="text-lg font-semibold text-slate-900">{formatCurrency(getInvoiceAmount(invoice))}</p>
                </div>
                
                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-400 transition-colors" />
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Invoice Detail Modal */}
      <AnimatePresence>
        {selectedInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setSelectedInvoice(null); setShowPdfPreview(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">{getSimpleInvoiceNumber(selectedInvoice.invoice_number)}</h2>
                    <p className="text-sm text-slate-500">{selectedInvoice.client_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${getStatusStyle(selectedInvoice.status)}`}>
                    {getStatusLabel(selectedInvoice.status)}
                  </span>
                  <button onClick={() => { setSelectedInvoice(null); setShowPdfPreview(false); }} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Info Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">{tx.client}</p>
                    <p className="font-medium text-slate-900">{selectedInvoice.client_name || 'N/A'}</p>
                    {selectedInvoice.client_email && (
                      <p className="text-sm text-slate-500 mt-0.5">{selectedInvoice.client_email}</p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">{tx.case}</p>
                    <p className="font-medium text-slate-900 truncate">
                      {getCaseName(selectedInvoice.case_id || selectedInvoice.case_ids?.[0]) || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">{tx.issueDate}</p>
                    <p className="font-medium text-slate-900">{formatFullDate(selectedInvoice.issue_date || selectedInvoice.created_at)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">{tx.dueDate}</p>
                    <p className="font-medium text-slate-900">{formatFullDate(selectedInvoice.due_date)}</p>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">{tx.lineItems}</p>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-xs text-slate-500">
                          <th className="px-4 py-3 text-left font-medium">{tx.description}</th>
                          <th className="px-4 py-3 text-right font-medium w-16">{tx.qty}</th>
                          <th className="px-4 py-3 text-right font-medium w-24">{tx.rate}</th>
                          <th className="px-4 py-3 text-right font-medium w-28">{tx.amount}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(selectedInvoice.line_items || selectedInvoice.items || []).length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-slate-400 text-sm">No items</td>
                          </tr>
                        ) : (
                          (selectedInvoice.line_items || selectedInvoice.items || []).map((item: any, idx: number) => (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-slate-900">{item.description}</td>
                              <td className="px-4 py-3 text-slate-600 text-right">{item.quantity || item.hours || 1}</td>
                              <td className="px-4 py-3 text-slate-600 text-right">{formatCurrency(item.unit_price || item.rate || 0)}</td>
                              <td className="px-4 py-3 text-slate-900 text-right font-medium">
                                {formatCurrency((item.quantity || item.hours || 1) * (item.unit_price || item.rate || 0))}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{tx.subtotal}</span>
                      <span className="text-slate-900">{formatCurrency(selectedInvoice.subtotal || getInvoiceAmount(selectedInvoice) * 0.8)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{tx.vat}</span>
                      <span className="text-slate-900">{formatCurrency(selectedInvoice.vat || getInvoiceAmount(selectedInvoice) * 0.2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-semibold pt-3 border-t border-slate-200">
                      <span className="text-slate-900">{tx.total}</span>
                      <span className="text-slate-900">{formatCurrency(getInvoiceAmount(selectedInvoice))}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between flex-shrink-0">
                <button
                  onClick={() => handleDelete(selectedInvoice.id)}
                  className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  {selectedInvoice.status?.toUpperCase() === 'DRAFT' && (
                    <button
                      onClick={() => handleSendInvoice(selectedInvoice.id)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      {tx.send}
                    </button>
                  )}
                  {selectedInvoice.status?.toUpperCase() === 'SENT' && (
                    <button
                      onClick={() => handleMarkPaid(selectedInvoice.id)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {tx.markPaid}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Invoice Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">{tx.newInvoice}</h2>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{tx.case}</label>
                  <select
                    value={formData.case_id}
                    onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-slate-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-slate-900"
                  >
                    <option value="">{tx.selectCase}</option>
                    {cases.map(c => (
                      <option key={c.id} value={c.id}>{c.topic}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{tx.dueDate}</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-slate-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{tx.lineItems}</label>
                  <div className="space-y-2">
                    {formData.items.map((item, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          type="text"
                          placeholder={tx.description}
                          value={item.description}
                          onChange={(e) => updateItem(idx, 'description', e.target.value)}
                          className="flex-1 px-3 py-2.5 bg-slate-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-slate-900"
                        />
                        <input
                          type="number"
                          placeholder={tx.hours}
                          value={item.hours}
                          onChange={(e) => updateItem(idx, 'hours', e.target.value)}
                          className="w-16 px-2 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-center focus:ring-2 focus:ring-slate-900"
                          step="0.25"
                        />
                        <input
                          type="number"
                          placeholder={tx.rate}
                          value={item.rate}
                          onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                          className="w-20 px-2 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-center focus:ring-2 focus:ring-slate-900"
                        />
                        {formData.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(idx)} className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addItem}
                    className="mt-3 text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    {tx.addItem}
                  </button>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    {tx.cancel}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-colors"
                  >
                    {tx.createInvoice}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoicingViewMinimal;
